import { SEAL_LABEL } from './vaultMotion';
import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import NearMeIcon from '@mui/icons-material/NearMe';
import PushPinIcon from '@mui/icons-material/PushPin';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchSystemIntel } from '../../api/intel';
import { fetchSystemNotes } from '../../api/systems';
import { setWaypoint } from '../../api/waypoint';
import Chevron from '../../components/Chevron';
import ConsolePanel from '../../components/ConsolePanel';
import Markdown from '../../components/Markdown';
import PanelHeader from '../../components/PanelHeader';
import ScanFrame from '../../components/ScanFrame';
import StatusBadge from '../../components/StatusBadge';
import { C, FONT, classColor, frameSx, hexToRgba, lcarsLabelSx } from '../../theme/tokens';
import { MapConnection, MapFolder, MapSystem, MassStatus, ShareScope, TrackedConnection } from '../../types';
import { AUTO_TAG, expiresInLabel, UNFILED } from './CompartmentList';
import ConnectionDiagram, { deriveObservedEdges } from './ConnectionDiagram';

const MASS_COLOR: Record<MassStatus, string> = {
  fresh: C.green,
  reduced: C.gold,
  critical: C.red,
  unknown: C.textDim,
};

/**
 * The vault as a briefing, not a form.
 *
 * Everything in this page used to be an editing surface — inputs, toggles
 * and save buttons — even for the reader who only wants to know what the
 * corp has scouted. A member checking "what do we know about this chain"
 * had to read their answers out of form controls, and a viewer (a real
 * read-only grant, see services/permissions.ts) had no honest view at all.
 *
 * So: reading is the default and gets its own rendering. Notes render as
 * markdown, not as textarea contents. Connections read as a chain. Nothing
 * here mutates anything, which is also why it is safe to show to a viewer
 * whose writes the API refuses.
 */
const ReaderView: React.FC<{
  vault: MapFolder;
  /** What the filter left. The cards and groups render from this. */
  systems: MapSystem[];
  /** Everything in the vault, filter or no filter — the header's counts
   * describe the vault, not the current search, and the chain has to be able
   * to name an endpoint the filter hid. */
  allSystems: MapSystem[];
  connections: MapConnection[];
  /** Hops the presence poller witnessed. The reader is the DEFAULT mode for
   * everyone, so leaving these out here meant the self-drawing half of the
   * diagram only ever appeared behind the edit-mode toggle — invisible to
   * exactly the people it orients. */
  tracked?: TrackedConnection[];
  query: string;
}> = ({ vault, systems, allSystems, connections, tracked, query }) => {
  const scopeKey = (vault.share_scope ?? 'private') as ShareScope;
  const home = allSystems.find((s) => s.is_home_system);
  const filtering = query.trim().length > 0;

  const groups = useMemo(() => {
    const byTag = new Map<string, MapSystem[]>();
    for (const s of systems) {
      const key = s.tag?.trim() || UNFILED;
      if (!byTag.has(key)) byTag.set(key, []);
      byTag.get(key)!.push(s);
    }
    // Same order the editor uses, so switching modes doesn't reshuffle the
    // page under the reader: home group first, auto and unfiled last.
    return [...byTag.entries()].sort(([a, aRows], [b, bRows]) => {
      const aHome = aRows.some((r) => r.is_home_system);
      const bHome = bRows.some((r) => r.is_home_system);
      if (aHome !== bHome) return aHome ? -1 : 1;
      if (a === UNFILED) return 1;
      if (b === UNFILED) return -1;
      if ((a === AUTO_TAG) !== (b === AUTO_TAG)) return a === AUTO_TAG ? 1 : -1;
      return a.localeCompare(b);
    });
  }, [systems]);

  // Resolved against every system, not the filtered set: a connection whose
  // far end the filter hid still has a name, and rendering it as "#42" would
  // be the filter corrupting the chain rather than narrowing it.
  const nameOf = (mapSystemId: number | null) => {
    if (!mapSystemId) return 'unknown';
    const s = allSystems.find((x) => x.id === mapSystemId);
    return s ? s.label || s.name : `#${mapSystemId}`;
  };

  // Only to decide whether the diagram has anything to show. The diagram
  // derives its own edges from the same inputs; this is the cheap pure call
  // repeated, not state to thread through.
  const observedEdges = useMemo(
    () => deriveObservedEdges(allSystems, connections, tracked),
    [allSystems, connections, tracked],
  );

  const openConnections = connections.filter((c) => !c.collapsed_at);
  const noted = allSystems.filter((s) => s.notes || s.notes_redacted).length;

  return (
    // minWidth:0 on the root, not just on the diagram's own scroll box: this
    // Stack is a flex item of the page, and without it the widest descendant
    // (the chain SVG) sets the whole column's width before any inner
    // overflow rule can take effect.
    <Stack spacing={2.5} sx={{ minWidth: 0 }}>
      {/* The vault's own header: what it is, who it's sealed to, and the
          three counts that answer "is this chain worth reading" before any
          of it is read. */}
      <ConsolePanel title={vault.name} accent={C.wormhole} corners={false}>
        <Stack spacing={1.75}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <StatusBadge label={SEAL_LABEL[scopeKey] ?? scopeKey} accent={C.wormhole} />
            {vault.auto_track === 1 && <StatusBadge label="auto-tracking · 12h" accent={C.gold} dim />}
          </Stack>

          {vault.description ? (
            <Markdown text={vault.description} />
          ) : (
            <Typography sx={{ color: C.textDim, fontSize: '0.82rem', fontStyle: 'italic' }}>
              No description written for this vault.
            </Typography>
          )}

          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Stat label="Systems" value={allSystems.length} />
            <Stat label="Groups" value={groups.length} />
            <Stat label="Open connections" value={openConnections.length} />
            <Stat label="With notes" value={noted} />
            <Stat label="Home" value={home ? home.label || home.name : '—'} mono />
          </Stack>
        </Stack>
      </ConsolePanel>

      {systems.length === 0 ? (
        <ScanFrame color={C.cyan} corners={false}>
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.84rem' }}>
            {filtering ? `Nothing in this vault matches \u201C${query}\u201D.` : 'Nothing saved in this vault yet.'}
          </Typography>
        </ScanFrame>
      ) : (
        groups.map(([tag, rows]) => (
          <Box key={tag}>
            <PanelHeader
              label={tag}
              accent={rows.some((r) => r.is_home_system) ? C.wormhole : C.cyan}
              dense
              sx={{ mb: 1.25 }}
              action={
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.textDim }}>
                  {rows.length} {rows.length === 1 ? 'system' : 'systems'}
                </Typography>
              }
            />
            {/* One card per system, filling the row rather than a fixed
                column count — a group of one shouldn't leave two empty
                slots beside it. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              {rows.map((s) => (
                <SystemCard key={s.id} system={s} connections={connections} nameOf={nameOf} />
              ))}
            </Box>
          </Box>
        ))
      )}

      {/* The diagram is read-only by construction — no positions, nothing
          draggable, recomputed from the connection list every render — so it
          belongs in the reader rather than behind an editor toggle. It shows
          the shape; the list underneath gives each edge's detail. */}
      {/* Gated on either kind of edge, not just logged ones. A vault whose
          chain came entirely from the tracker — nobody logged a thing by
          hand — is precisely the case the self-drawing diagram exists for,
          and `connections.length > 0` alone hid the diagram exactly there.
          It has to be the DERIVED edges, though, not the raw tracked list:
          /tracked reports every hop the corp flew in the last 48h, corp-wide
          and not scoped to this vault, so testing its length would open an
          empty diagram panel on an unrelated vault whenever anybody was
          flying. deriveObservedEdges is what drops the hops this vault has
          nowhere to draw. */}
      {(connections.length > 0 || observedEdges.length > 0) && (
        <ConnectionDiagram systems={allSystems} connections={connections} tracked={tracked} />
      )}

      {connections.length > 0 && (
        <Box>
          <PanelHeader label="Chain" accent={C.cyan} dense sx={{ mb: 1.25 }} />
          <ScanFrame color={C.cyan} corners={false}>
            <Stack spacing={0.9}>
              {connections.map((c) => (
                <Stack
                  key={c.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ opacity: c.collapsed_at ? 0.45 : 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT.mono,
                      fontSize: '0.84rem',
                      color: C.text,
                      textDecoration: c.collapsed_at ? 'line-through' : 'none',
                    }}
                  >
                    {nameOf(c.from_map_system_id)} &rarr; {nameOf(c.to_map_system_id)}
                  </Typography>
                  {c.signature_code && (
                    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>
                      {c.signature_code}
                    </Typography>
                  )}
                  <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: MASS_COLOR[c.mass_status] }}>
                    {c.mass_status} mass
                  </Typography>
                  {c.life_status === 'eol' && <StatusBadge label="EOL" accent={C.red} />}
                  {c.collapsed_at && <StatusBadge label="collapsed" accent={C.textDim} dim />}
                  {c.notes && (
                    <Typography sx={{ fontSize: '0.76rem', color: C.textDim }}>{c.notes}</Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          </ScanFrame>
        </Box>
      )}
    </Stack>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <Box>
    <Typography sx={lcarsLabelSx(C.textDim, '0.62rem')}>{label}</Typography>
    <Typography
      sx={{ fontFamily: mono ? FONT.mono : FONT.sub, fontWeight: 700, fontSize: '1.05rem', color: C.textBright, mt: 0.25 }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * One saved system, read rather than edited: what it is, what the corp
 * wrote about it, what it connects to, and what the SDE knows.
 *
 * ponytail: two requests per card (intel + corp notes), so a vault opens with
 * 2N in flight where the editor only paid for intel. React Query dedupes by
 * key, so a system in two vaults or revisited across a mode switch is free,
 * and at corp vault sizes (tens) this is fine. If a forty-system vault ever
 * feels slow on open, the fix is one batched
 * `GET /api/maps/:id/systems?expand=intel,notes` — not caching around the
 * edges of this component.
 */
const SystemCard: React.FC<{
  system: MapSystem;
  connections: MapConnection[];
  nameOf: (id: number | null) => string;
}> = ({ system, connections, nameOf }) => {
  const navigate = useNavigate();
  // Setting your own autopilot is a personal action, not a change to corp
  // data — so it stays available to a read-only grant, the same way a
  // personal star does.
  const waypoint = useMutation({ mutationFn: () => setWaypoint(system.solar_system_id) });
  const { data: intel } = useQuery({
    queryKey: ['intel', 'system', system.solar_system_id],
    queryFn: () => fetchSystemIntel(system.solar_system_id),
  });
  // Corp-wide notes are keyed to the system rather than this vault, and a
  // reader wants both without hunting: the vault's own note says why THIS
  // chain cares, the corp note says what the system is.
  const { data: corpNotes } = useQuery({
    queryKey: ['system-notes', system.solar_system_id],
    queryFn: () => fetchSystemNotes(system.solar_system_id),
  });

  const accent = system.is_home_system ? C.wormhole : C.cyan;
  const links = connections.filter(
    (c) => c.from_map_system_id === system.id || c.to_map_system_id === system.id,
  );
  const statics = intel?.statics.length
    ? intel.statics.map((s) => s.static_type_code).join(', ')
    : intel?.staticsCatalogued === false
      ? 'uncatalogued'
      : '…';

  return (
    <Box sx={{ ...frameSx(accent), p: 2, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chevron color={accent} />
        <Typography sx={{ fontFamily: FONT.mono, fontWeight: 600, fontSize: '0.95rem', color: C.text }}>
          {system.label || system.name}
        </Typography>
        {system.label && system.label !== system.name && (
          <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>{system.name}</Typography>
        )}
        {system.system_class && (
          <StatusBadge label={system.system_class} accent={classColor(system.system_class)} />
        )}
        {system.is_home_system ? (
          <StatusBadge label="HOME" accent={C.wormhole} icon={<HomeIcon sx={{ fontSize: 11, color: C.wormhole }} />} />
        ) : null}
        {system.is_pinned ? (
          <StatusBadge label="pinned" accent={C.gold} icon={<PushPinIcon sx={{ fontSize: 10, color: C.gold }} />} />
        ) : null}
        {system.auto_expires_at && (
          <StatusBadge label={`auto · ${expiresInLabel(system.auto_expires_at)}`} accent={C.gold} dim />
        )}
        <Box sx={{ flex: 1 }} />
        {/* Two actions, and they must be two different actions: these both
            navigated to the same page at first, which is an icon pretending
            to be a second capability. Open intel, or fly there. */}
        <Box
          component="button"
          type="button"
          title="Open full intel"
          onClick={() => navigate(`/system/${encodeURIComponent(system.name)}`)}
          sx={iconBtnSx}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box
          component="button"
          type="button"
          title={waypoint.isSuccess ? 'Destination set' : 'Set destination'}
          disabled={waypoint.isPending}
          onClick={() => waypoint.mutate()}
          sx={{ ...iconBtnSx, color: waypoint.isSuccess ? C.cyan : C.textDim }}
        >
          <NearMeIcon sx={{ fontSize: 16 }} />
        </Box>
      </Stack>

      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.74rem', color: C.textDim, mb: 1.25 }}>
        {intel?.effect?.label ?? 'no effect'} &middot; statics: {statics}
      </Typography>

      {links.length > 0 && (
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem'), mb: 0.5 }}>Connects to</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {links.map((c) => {
              const other = c.from_map_system_id === system.id ? c.to_map_system_id : c.from_map_system_id;
              return (
                <StatusBadge
                  key={c.id}
                  label={`${nameOf(other)}${c.collapsed_at ? ' · collapsed' : ''}`}
                  accent={c.collapsed_at ? C.textDim : MASS_COLOR[c.mass_status]}
                  dim={!!c.collapsed_at}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      <Section label="Vault note">
        {system.notes_redacted ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <LockIcon sx={{ fontSize: 13, color: C.textDim }} />
            <Typography sx={emptySx}>Classified by its author.</Typography>
          </Stack>
        ) : system.notes ? (
          <Markdown text={system.notes} dense />
        ) : (
          <Typography sx={emptySx}>Nothing written yet.</Typography>
        )}
      </Section>

      {corpNotes && corpNotes.length > 0 && (
        <Section label="Corp notes">
          <Stack spacing={1}>
            {corpNotes.map((n) => (
              <Box key={n.id}>
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: C.gold, mb: 0.25 }}>
                  {n.ownerName ?? `#${n.ownerCharacterId}`}
                </Typography>
                <Markdown text={n.body} dense />
              </Box>
            ))}
          </Stack>
        </Section>
      )}
    </Box>
  );
};

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${C.hairline}` }}>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem'), mb: 0.5 }}>{label}</Typography>
    {children}
  </Box>
);

const emptySx = { color: C.textDim, fontSize: '0.8rem', fontStyle: 'italic' } as const;

const iconBtnSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  p: 0,
  borderRadius: '4px',
  cursor: 'pointer',
  background: 'transparent',
  border: '1px solid transparent',
  color: C.textDim,
  transition: 'color 0.15s ease, border-color 0.15s ease',
  '&:hover:not(:disabled)': { color: C.cyan, borderColor: hexToRgba(C.cyan, 0.4) },
  '&:disabled': { opacity: 0.35, cursor: 'default' },
} as const;

export default ReaderView;
